import {
  ApplicationRef,
  ComponentRef,
  EnvironmentInjector,
  Injectable,
  NgZone,
  OnDestroy,
  createComponent,
  inject,
} from '@angular/core';
import { OskGuideButtonComponent } from '../../components/osk-guide/osk-guide-button/osk-guide-button.component';
import { isOskBoss } from '../helpers/osk-staff-role';
import { OskGuideService } from './osk-guide.service';

/** Right part of the fas-admin top bar: the account button, then logout. */
const TOP_BAR_RIGHT_SELECTOR = 'lib-fas-admin .top-bar > .btn-wraper:last-child';

/**
 * Puts the "Instrukcja" button (OskGuideButtonComponent) into the fas-admin top bar, left of the account
 * button - for the OSK owner only. The library renders the top bar and has no slot for extra buttons, so the
 * button is created here and attached to the application, like MobileShellService drives the side menu.
 * The layout is created after every login and dropped on logout, so a MutationObserver follows it: a new top bar
 * gets the button if the account in the token is the owner, a removed one takes it along.
 * Started once from AppComponent.
 */
@Injectable({ providedIn: 'root' })
export class OskGuideTopBarService implements OnDestroy {
  private readonly zone = inject(NgZone);
  private readonly appRef = inject(ApplicationRef);
  private readonly injector = inject(EnvironmentInjector);
  private readonly guide = inject(OskGuideService);
  private observer?: MutationObserver;
  private button?: ComponentRef<OskGuideButtonComponent>;
  private topBar: Element | null = null;
  private started = false;

  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    this.zone.runOutsideAngular(() => {
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(document.body, { childList: true, subtree: true });
    });
    this.sync();
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.removeButton();
  }

  // Runs on every DOM change: it does work only when the top bar came, went or lost the button.
  private sync(): void {
    const topBar = document.querySelector(TOP_BAR_RIGHT_SELECTOR);
    const buttonLost = this.button !== undefined && !this.button.location.nativeElement.isConnected;
    if (topBar === this.topBar && !buttonLost) {
      return;
    }
    this.topBar = topBar;
    this.zone.run(() => {
      this.removeButton();
      if (topBar && isOskBoss()) {
        this.addButton(topBar);
      }
    });
  }

  private addButton(topBar: Element): void {
    this.guide.refresh();
    const host = document.createElement('app-osk-guide-button');
    topBar.insertBefore(host, topBar.firstChild);
    this.button = createComponent(OskGuideButtonComponent, {
      environmentInjector: this.injector,
      hostElement: host,
    });
    this.appRef.attachView(this.button.hostView);
  }

  private removeButton(): void {
    if (!this.button) {
      return;
    }
    const host: Element = this.button.location.nativeElement;
    this.appRef.detachView(this.button.hostView);
    this.button.destroy();
    host.remove();
    this.button = undefined;
  }
}
